/**
 * `AgentPreloadEditor` — view + edit one agent preload file (CLAUDE.md or
 * SKILL.md), showing baseline vs override vs effective.
 *
 * LIFTED out of `Preload.tsx` on 2026-08-11 when migration 166 widened
 * `pa_agent_preload` from the deep worker alone to peter / inbox / trainerops,
 * and `/agents` became the second page that edits them. It gains one prop —
 * `agent` — and is otherwise the same component; `Preload.tsx` imports it back.
 *
 * WHY A MOVE RATHER THAN A COPY. The `preload_guard_stripped` branch turns a
 * raw error code into the sentence an operator can act on ("keep the guard
 * directives and try again"), and the reset path confirms before dropping an
 * override. A second copy that missed either would show a bare code where the
 * explanation belongs, on a screen whose whole subject is safety guards.
 *
 * WHAT IT DELIBERATELY DOES NOT DO: edit `GUARDS.md`. That file is not in
 * `PRELOAD_FILES` and never will be — see the argument there.
 */

import { useState, type CSSProperties, type JSX } from "react";

import { dispatchAction } from "../lib/dispatch";

/** One preload file's three layers, as `GET /api/preload` returns them. */
export interface AgentPreloadFile {
  readonly file: "CLAUDE.md" | "SKILL.md";
  /** The git mirror shipped on the image. */
  readonly baseline: string;
  /** The live DB override, or null when the agent is on the git default. */
  readonly override: string | null;
  /** `override ?? baseline` — what the runner materializes. */
  readonly effective: string;
  readonly updated_at: string | null;
  readonly updated_by: string | null;
}

export function AgentPreloadEditor({
  agent,
  file,
  onSaved,
  disabled = false,
}: {
  /** The `pa_agent_preload.agent` key: worker | peter | inbox | trainerops. */
  readonly agent: string;
  readonly file: AgentPreloadFile;
  readonly onSaved: () => Promise<void>;
  /** Set when nothing reads this override today; the editor still saves. */
  readonly disabled?: boolean;
}): JSX.Element {
  const [editing, setEditing] = useState<boolean>(false);
  const [draft, setDraft] = useState<string>(file.effective);
  const [busy, setBusy] = useState<boolean>(false);
  const [err, setErr] = useState<string | null>(null);
  const overridden = file.override !== null;

  const save = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    const res = await dispatchAction<Record<string, unknown>, unknown>(
      "set_agent_preload",
      { agent, file: file.file, content: draft },
    );
    setBusy(false);
    if (!res.ok) {
      setErr(res.code);
      return;
    }
    setEditing(false);
    await onSaved();
  };

  const reset = async (): Promise<void> => {
    if (
      !window.confirm(
        `Revert ${agent}'s ${file.file} to the git default (drop the override)?`,
      )
    ) {
      return;
    }
    setBusy(true);
    setErr(null);
    const res = await dispatchAction<Record<string, unknown>, unknown>(
      "set_agent_preload",
      { agent, file: file.file, content: file.baseline, reset: true },
    );
    setBusy(false);
    if (!res.ok) {
      setErr(res.code);
      return;
    }
    setEditing(false);
    await onSaved();
  };

  return (
    <div
      style={{
        marginBottom: 16,
        borderTop: "1px solid var(--c-line-92-c)",
        paddingTop: 12,
      }}
    >
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <code style={codeStyle}>{file.file}</code>
        {overridden ? (
          <span className="tag tag-success">override active</span>
        ) : (
          <span className="tag">git default</span>
        )}
        {file.updated_at !== null && (
          <span style={{ fontSize: "0.8em", opacity: 0.7 }}>
            edited {file.updated_at.slice(0, 16).replace("T", " ")}
            {file.updated_by !== null ? ` by ${file.updated_by}` : ""}
          </span>
        )}
        <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          {!editing && (
            <button
              type="button"
              onClick={() => {
                setDraft(file.effective);
                setEditing(true);
              }}
            >
              Edit
            </button>
          )}
          {overridden && (
            <button type="button" onClick={() => void reset()} disabled={busy}>
              Reset to git default
            </button>
          )}
        </span>
      </div>

      {err !== null && (
        <p
          style={{
            color: "var(--c-danger-text-48)",
            fontSize: "0.85em",
            margin: "6px 0 0",
          }}
        >
          {err === "preload_guard_stripped"
            ? "Rejected: the edit dropped a required safety guard (this agent's confirm-guard markers). Keep the guard directives and try again."
            : `Error: ${err}`}
        </p>
      )}

      {editing ? (
        <div style={{ marginTop: 8 }}>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            spellCheck={false}
            style={editorStyle}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
            <button
              type="button"
              onClick={() => void save()}
              disabled={busy || draft.trim().length === 0}
            >
              {disabled ? "Save override (stored, not yet read)" : "Save override"}
            </button>
            <button type="button" onClick={() => setEditing(false)} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <ReadOnlyBlock label="effective" text={file.effective} />
      )}
    </div>
  );
}

/** Collapsible read-only block with a char count. Lifted alongside the editor. */
export function ReadOnlyBlock({
  label,
  text,
}: {
  readonly label: string;
  readonly text: string;
}): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  return (
    <div style={{ marginTop: 6 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          font: "inherit",
        }}
      >
        {open ? "▼" : "▶"} {label}{" "}
        <span style={{ opacity: 0.6, fontSize: "0.85em" }}>
          ({text.length.toLocaleString()} chars)
        </span>
      </button>
      {open && (
        <pre style={preStyle}>{text.length > 0 ? text : "(baseline unavailable)"}</pre>
      )}
    </div>
  );
}

const codeStyle: CSSProperties = {
  fontFamily: "var(--mono, monospace)",
  fontSize: "0.9em",
  background: "var(--c-tint-grey-12)",
  padding: "2px 6px",
  borderRadius: 4,
};
const preStyle: CSSProperties = {
  margin: "6px 0 0",
  padding: 10,
  background: "var(--c-tint-grey-12)",
  borderRadius: 6,
  overflowX: "auto",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  fontSize: "0.8em",
  maxHeight: 420,
  overflowY: "auto",
};
const editorStyle: CSSProperties = {
  width: "100%",
  minHeight: 300,
  fontFamily: "var(--mono, monospace)",
  fontSize: "0.82em",
  padding: 10,
  boxSizing: "border-box",
};
